import { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Loader2, Calendar, Users, MapPin, Sun, Fish, Compass, ArrowLeft, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import TripBookingForm from '../components/TripBookingForm';
import { TripInquiryModal } from '../components/TripInquiryModal';
import { formatItemPrice } from '../lib/currency';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * TripDetailPage — full-page view of a single trip at /trips/:id
 *
 * Loaded directly from /api/trips/:id (independent of the trips listing page),
 * so a shared link can never accidentally land on the wrong place.
 *
 * Includes the full info block (image, dates, accommodation, dive site, sightings,
 * weather, activities, inclusions), Book/Inquire CTA, and Share button that
 * shares THIS PAGE's URL.
 */
const TripDetailPage = () => {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToCart } = useCart();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [accommIdx, setAccommIdx] = useState(0);
  const [selectedDest, setSelectedDest] = useState('');
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showInquiryModal, setShowInquiryModal] = useState(false);

  const fetchTrip = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/trips/${tripId}`);
      setTrip(res.data);
      document.title = `${res.data.destination} — Just Dive`;
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
      else toast.error('Failed to load trip');
    } finally {
      setLoading(false);
    }
  }, [tripId]);

  useEffect(() => { fetchTrip(); }, [fetchTrip]);

  const opts = trip?.accommodation_options || [];
  const chosen = opts[accommIdx];
  const priceVal = chosen?.price ?? trip?.price;
  // Default to per-person-sharing (matches backend model default) when a
  // trip has no accommodation option chosen. "pp" is only used for trips
  // that have explicitly set it.
  const basis = chosen?.pricing_basis || 'pps';
  const basisLabel = { pps: 'per person sharing', pp: 'per person', single: 'single (solo room)' }[basis] || 'per person sharing';
  const fromPrefix = (opts.length > 1 || trip?.show_starting_from) ? 'starting from ' : '';
  const isMultiDest = !!(trip?.destinations && trip.destinations.length >= 2);

  const share = async () => {
    if (!trip) return;
    // Share via the server-rendered OG endpoint so WhatsApp / FB / iMessage get
    // a proper preview (correct title, description and hero image). The endpoint
    // redirects real visitors to /trips/:id. The `?v=` cache-buster forces
    // WhatsApp to fetch a fresh preview after the admin edits the trip — WA
    // caches previews by exact URL so changing the query string defeats it.
    const apiBase = process.env.REACT_APP_BACKEND_URL.replace(/\/$/, '');
    const ver = encodeURIComponent(trip.updated_at || trip.created_at || Date.now());
    const url = `${apiBase}/api/share/trip/${trip.id}?v=${ver}`;
    const fromText = (opts.length > 1 || trip.show_starting_from) ? 'from ' : '';
    const dateLine = trip.inquire_only
      ? 'Dates: TBC — register your interest below'
      : (trip.display_date || `${trip.start_date} - ${trip.end_date}`);
    const priceLine = priceVal
      ? `${fromText}${formatItemPrice(priceVal, trip.currency, { compact: true })} ${({ pps: 'pps', pp: 'pp', single: 'single' }[basis] || 'pps')}${trip.inclusions ? ` · ${trip.inclusions}` : ''}`
      : 'Price on request';
    const text = [
      `🤿 Check out this dive trip with Just Dive!`,
      ``,
      `*${trip.destination}*`,
      dateLine,
      priceLine,
      ``,
      url,
    ].join('\n');
    // Prefer the native share sheet on mobile so users can pick WhatsApp / SMS / Email
    if (navigator.share) {
      try {
        await navigator.share({ title: trip.destination, text, url });
        return;
      } catch (_) { /* user cancelled, fall through */ }
    }
    try { await navigator.clipboard.writeText(url); toast.success('Link copied — opening WhatsApp…'); } catch (_) {}
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer');
  };

  const handleBook = () => {
    if (!user) { navigate('/auth'); return; }
    if (isMultiDest && !selectedDest) {
      toast.error('Please choose a destination first');
      return;
    }
    setShowBookingModal(true);
  };

  const handleBookingSubmit = async (bookingData) => {
    const pricePerPerson = chosen?.price ?? trip.price;
    const effectiveDestination = selectedDest || trip.destination;
    const cartItem = {
      id: `trip-${trip.id}-${Date.now()}`,
      tripId: trip.id,
      name: `Trip: ${effectiveDestination}${chosen ? ` — ${chosen.name}` : ''}`,
      description: `${trip.display_date || `${trip.start_date} - ${trip.end_date}`} • ${bookingData.attendeeCount} ${bookingData.attendeeCount === 1 ? 'person' : 'people'}${chosen ? ` • ${chosen.name}` : ''}${bookingData.gearHireCost > 0 ? ' • Gear Hire Included' : ''}${bookingData.cateringOpted ? ' • Catered' : ''}`,
      price: bookingData.totalPrice,
      image_url: trip.image_url,
      category: 'trip',
      isTrip: true,
      type: 'trip',
      trip_id: trip.id,
      trip_destination: effectiveDestination,
      selectedDestination: selectedDest || null,
      selected_destination: selectedDest || null,
      accommodation: chosen?.name,
      currency: trip.currency,
      attendeeCount: bookingData.attendeeCount,
      pricePerPerson,
      tripPrice: bookingData.tripPrice,
      gearHire: bookingData.gearHire,
      gearHireCost: bookingData.gearHireCost || 0,
      gearSummary: bookingData.gearSummary,
      cateringOpted: !!bookingData.cateringOpted,
      cateringCost: bookingData.cateringCost || 0,
      cateringPricePerPerson: bookingData.cateringPricePerPerson || 0,
      tripDays: bookingData.tripDays,
      bookingDetails: bookingData.formData,
      medicalForms: {
        primary: {
          responses: bookingData.formData.medical,
          signature: bookingData.formData.medicalSignatureName,
          signatureDate: bookingData.formData.medicalSignatureDate,
        },
        additionalAttendees: (bookingData.formData.additionalAttendees || []).map(att => ({
          name: att.name,
          responses: att.medical,
          signature: att.medicalSignatureName,
          signatureDate: att.medicalSignatureDate,
        })),
      },
      waiverDetails: {
        accepted: bookingData.formData.waiverAccepted,
        signature: bookingData.formData.waiverSignatureName,
        signatureDate: bookingData.formData.waiverSignatureDate,
      },
    };
    addToCart(cartItem);
    setShowBookingModal(false);
    toast.success('Trip booking added to cart');
    navigate('/cart');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <Loader2 className="w-8 h-8 animate-spin text-[#0A2342]" />
      </div>
    );
  }

  if (notFound || !trip) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] p-6">
        <div className="max-w-md text-center">
          <h1 className="text-3xl font-heading font-bold text-[#0A2342] mb-3">Trip not found</h1>
          <p className="text-gray-600 mb-6">
            This trip may have been removed or the link is no longer valid.
          </p>
          <Button asChild className="bg-[#FF6B35] hover:bg-[#FF6B35]/90">
            <Link to="/trips" data-testid="back-to-trips-btn">See all trips</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-10 bg-[#FAFAFA]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        {/* Back link */}
        <Link to="/trips" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-[#0A2342] mb-4" data-testid="back-link">
          <ArrowLeft className="w-4 h-4" /> All trips
        </Link>

        <div className="bg-white border border-gray-100 rounded-lg overflow-hidden shadow-sm" data-testid={`trip-detail-${trip.id}`}>
          {/* Hero image — keeps natural aspect ratio (portrait posters render full-height, landscape full-width) */}
          <div className="bg-gray-100 flex items-center justify-center overflow-hidden relative max-h-[80vh]">
            <img
              src={trip.image_url}
              alt={trip.destination}
              className="w-auto h-auto max-w-full max-h-[80vh] block"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                const sib = e.currentTarget.nextElementSibling;
                if (sib) sib.style.display = 'flex';
              }}
            />
            <div
              className="hidden items-center justify-center bg-gradient-to-br from-[#0A2342] to-[#1B4F8E] text-white text-center px-6 w-full aspect-video"
              style={{ display: 'none' }}
              data-testid="trip-image-fallback"
            >
              <div>
                <p className="text-xs uppercase tracking-widest opacity-70 mb-2">Image pending</p>
                <p className="font-heading text-3xl font-bold">{trip.destination}</p>
                <p className="text-xs opacity-60 mt-3">Admin: re-upload trip image</p>
              </div>
            </div>
          </div>

          <div className="p-6 sm:p-8">
            {/* Title + badges */}
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-3xl sm:text-4xl font-heading font-bold text-[#0A2342]">
                  {isMultiDest ? trip.destinations.join(' or ') : trip.destination}
                </h1>
                {trip.available_on_pro && <Badge className="bg-purple-600 text-white">PRO</Badge>}
                {trip.inquire_only && <Badge className="bg-amber-500 text-white">Dates TBC</Badge>}
                {trip.catered && (
                  <Badge className="bg-[#FF6B35] text-white" data-testid="catered-badge">
                    🍴 Catered{(trip.catered_price ?? 0) > 0 ? ` +R${Number(trip.catered_price).toLocaleString()}` : ''}
                  </Badge>
                )}
                {trip.self_catering && (
                  <Badge className="bg-[#0A2342] text-white" data-testid="self-catered-badge">
                    🥫 Self-Catered
                  </Badge>
                )}
              </div>
            </div>

            {/* In a Nutshell — summary block at the top of the details.
                Only rendered when the admin has filled it in. */}
            {trip.nutshell && (
              <div className="bg-orange-50 border-l-4 border-[#FF6B35] rounded p-4 mb-6" data-testid="nutshell-block">
                <p className="text-[11px] font-bold uppercase tracking-wider text-[#FF6B35] mb-1">In a Nutshell</p>
                <p className="text-[#0A2342] whitespace-pre-line leading-relaxed">{trip.nutshell}</p>
              </div>
            )}

            <p className="text-gray-700 mb-6 text-base sm:text-lg">{trip.description}</p>

            {/* Meta row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6 text-sm">
              {trip.inquire_only ? (
                <div className="flex items-center gap-2 text-[#FF6B35]">
                  <Calendar className="w-4 h-4" />
                  <span>Dates TBC — register your interest below</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-gray-700">
                  <Calendar className="w-4 h-4" />
                  <span>{trip.display_date || `${trip.start_date} - ${trip.end_date}`}</span>
                </div>
              )}
              {trip.max_participants && !trip.inquire_only && (
                <div className="flex items-center gap-2 text-gray-700">
                  <Users className="w-4 h-4" /> Max {trip.max_participants} participants
                </div>
              )}
              {trip.dive_site && (
                <div className="flex items-center gap-2 text-gray-700">
                  <MapPin className="w-4 h-4" /> {trip.dive_site}
                </div>
              )}
            </div>

            {/* Multi-destination picker — locked at booking, paid up-front. */}
            {isMultiDest && !trip.inquire_only && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6" data-testid="destination-block">
                <h3 className="text-sm font-semibold text-[#0A2342] mb-2 uppercase tracking-wide">Choose your destination</h3>
                <select
                  value={selectedDest}
                  onChange={(e) => setSelectedDest(e.target.value)}
                  className="w-full border rounded p-2 text-sm bg-white"
                  data-testid="destination-select"
                >
                  <option value="">— Select destination —</option>
                  {trip.destinations.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
                <p className="text-[11px] text-gray-600 mt-2">Same trip dates and price — you just pick where you dive. Your choice is locked at booking.</p>
              </div>
            )}

            {/* Accommodation options */}
            {opts.length > 0 && (
              <div className="bg-[#F5F7FB] border border-gray-200 rounded-lg p-4 mb-6" data-testid="accommodation-block">
                <h3 className="text-sm font-semibold text-[#0A2342] mb-2">Accommodation options</h3>
                <select
                  value={accommIdx}
                  onChange={(e) => setAccommIdx(parseInt(e.target.value, 10))}
                  className="w-full border rounded p-2 text-sm bg-white"
                  data-testid="accommodation-select"
                >
                  {opts.map((opt, i) => {
                    const basisShort = { pps: 'pps', pp: 'pp', single: 'single' }[opt.pricing_basis || 'pps'] || 'pps';
                    return (
                      <option key={i} value={i}>
                        {opt.name} — {formatItemPrice(opt.price, trip.currency, { compact: true })} {basisShort}
                      </option>
                    );
                  })}
                </select>
                {chosen?.description && <p className="text-xs text-gray-600 mt-2">{chosen.description}</p>}
              </div>
            )}

            {/* Extra info blocks */}
            {(trip.sighting_possibilities || trip.typical_weather || trip.area_activities) && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                {trip.sighting_possibilities && (
                  <div className="bg-blue-50 border border-blue-100 rounded p-3 text-sm">
                    <div className="flex items-center gap-2 font-semibold text-blue-900 mb-1"><Fish className="w-4 h-4" /> Sightings</div>
                    <p className="text-blue-800">{trip.sighting_possibilities}</p>
                  </div>
                )}
                {trip.typical_weather && (
                  <div className="bg-amber-50 border border-amber-100 rounded p-3 text-sm">
                    <div className="flex items-center gap-2 font-semibold text-amber-900 mb-1"><Sun className="w-4 h-4" /> Weather</div>
                    <p className="text-amber-800">{trip.typical_weather}</p>
                  </div>
                )}
                {trip.area_activities && (
                  <div className="bg-emerald-50 border border-emerald-100 rounded p-3 text-sm">
                    <div className="flex items-center gap-2 font-semibold text-emerald-900 mb-1"><Compass className="w-4 h-4" /> Activities</div>
                    <p className="text-emerald-800">{trip.area_activities}</p>
                  </div>
                )}
              </div>
            )}

            {trip.inclusions && (
              <p className="text-sm text-gray-600 mb-6">
                <strong>Includes:</strong> {trip.inclusions}
              </p>
            )}

            {/* Price + CTAs */}
            <div className="border-t border-gray-200 pt-6 flex flex-wrap items-end justify-between gap-4">
              <div>
                {fromPrefix && <div className="text-xs text-gray-500 mb-1">{fromPrefix}</div>}
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-3xl font-mono-accent font-bold text-[#0A2342]">
                    {priceVal ? formatItemPrice(priceVal, trip.currency, { compact: true }) : 'Price on request'}
                  </span>
                  {priceVal && <span className="text-sm text-gray-500">{basisLabel}</span>}
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  variant="outline"
                  onClick={share}
                  data-testid="share-trip-btn"
                  className="border-[#0A2342] text-[#0A2342]"
                >
                  <Share2 className="w-4 h-4 mr-2" /> Share
                </Button>
                {trip.inquire_only ? (
                  <Button
                    onClick={() => setShowInquiryModal(true)}
                    className="bg-[#0A2342] text-white hover:bg-[#0A2342]/90"
                    data-testid="inquire-trip-btn"
                  >
                    Inquire Now
                  </Button>
                ) : (() => {
                  const fullyBooked = trip.max_participants && trip.current_participants >= trip.max_participants;
                  const noDestPicked = isMultiDest && !selectedDest;
                  let label = 'Book Trip';
                  if (!user) label = 'Sign In to Book';
                  else if (fullyBooked) label = 'Fully Booked';
                  else if (noDestPicked) label = 'Pick a destination';
                  return (
                    <Button
                      onClick={handleBook}
                      disabled={fullyBooked || noDestPicked}
                      className="bg-[#FF6B35] text-white hover:bg-[#FF6B35]/90"
                      data-testid="book-trip-btn"
                    >
                      {label}
                    </Button>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Booking modal */}
      <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading text-[#0A2342]">Book Trip</DialogTitle>
          </DialogHeader>
          {trip && user && (
            <TripBookingForm
              trip={{
                ...trip,
                accommodation_option: chosen,
                // When the diver picked a specific destination on this
                // multi-destination trip, propagate it so the booking form,
                // cart line and confirmation all use the specific location
                // instead of the "Sodwana or Ponta" umbrella.
                selected_destination: selectedDest || null,
                destination: selectedDest || trip.destination,
              }}
              user={user}
              onSubmit={handleBookingSubmit}
              onCancel={() => setShowBookingModal(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Inquiry modal */}
      {trip && (
        <TripInquiryModal
          trip={trip}
          open={showInquiryModal}
          onOpenChange={setShowInquiryModal}
          defaultName={user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : ''}
          defaultEmail={user?.email || ''}
          defaultPhone={user?.contact_number || ''}
        />
      )}
    </div>
  );
};

export default TripDetailPage;
